import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

const Privacy = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <Link to="/play" className="back-link">
          ← Back to Game
        </Link>

        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-updated">Last Updated: November 6, 2025</p>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Carnivores Adapting Terribly LLC ("we", "us", or "our"). We respect your
              privacy and are committed to protecting your personal information. This Privacy Policy
              explains how we collect, use, and safeguard your information when you use our game at
              carnivoresadaptingterribly.com.
            </p>
            {/* TODO: After LLC formation, add business address here */}
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide</h3>
            <ul>
              <li>
                <strong>Username:</strong> The display name you choose when playing
              </li>
              <li>
                <strong>Email Address:</strong> Only if you contact us through our contact form
              </li>
              <li>
                <strong>Feedback:</strong> Any messages, bug reports, or suggestions you submit
              </li>
              <li>
                <strong>Write-In Cards:</strong> Temporary text content you create during gameplay
                (deleted when games end)
              </li>
              <li>
                <strong>Custom Deck Content:</strong> User-created card text (when feature is
                available, deleted when games end)
              </li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <ul>
              <li>
                <strong>IP Address:</strong> Used for rate limiting and abuse prevention
              </li>
              <li>
                <strong>Browser Information:</strong> Browser type and version for compatibility
              </li>
              <li>
                <strong>Session Data:</strong> Temporary game room codes and game states
              </li>
              <li>
                <strong>Usage Statistics:</strong> Games played, rounds completed (not linked to
                personal identity)
              </li>
            </ul>

            <h3>2.3 Information Stored Locally</h3>
            <ul>
              <li>
                <strong>localStorage:</strong> Your username, age verification status, and active
                game session information stored in your browser
              </li>
              <li>
                <strong>Cookies:</strong> We do not currently use cookies
              </li>
            </ul>

            <h3>2.4 OAuth Authentication (Google & Discord)</h3>
            <p>When you log in using Google or Discord, we collect:</p>
            <ul>
              <li>
                <strong>Email address:</strong> Used to link accounts across providers
              </li>
              <li>
                <strong>Username:</strong> Display name from your Google or Discord account
              </li>
              <li>
                <strong>Profile picture:</strong> Avatar from your Google or Discord account
              </li>
              <li>
                <strong>Unique user ID:</strong> OAuth provider's user identifier (not your
                password)
              </li>
            </ul>
            <p>
              <strong>We do NOT have access to your Google or Discord password.</strong>{' '}
              Authentication is handled securely by Google and Discord using industry-standard OAuth
              2.0 protocol. We only receive basic profile information that you explicitly authorize
              us to access.
            </p>

            <h3>2.5 Persistent User Identity & Cross-Device Access</h3>
            <p>When you authenticate with Google or Discord OAuth, we provide:</p>
            <ul>
              <li>
                <strong>Permanent User ID:</strong> A unique identifier linked to your OAuth account
                (e.g., "google_1234567890") that persists across sessions and devices
              </li>
              <li>
                <strong>JWT Tokens:</strong> Secure authentication tokens stored in your browser to
                maintain your identity
              </li>
              <li>
                <strong>Cross-Device Persistence:</strong> Your game data (scores, saved games)
                follows you across devices when using the same OAuth account
              </li>
            </ul>
            <p>
              <strong>Guest Users:</strong> If you play without logging in, you receive a temporary
              user ID that is lost when you close your browser. Guest accounts cannot be recovered
              and do not persist across devices.
            </p>
            <p>
              <strong>Data Retention:</strong> OAuth user data is retained for 90 days of
              inactivity, after which it is automatically deleted. Guest user data is deleted when
              the browser session ends.
            </p>
          </section>

          <section className="privacy-section">
            <h2>3. Information We DO NOT Collect</h2>
            <p className="highlight-text">
              We want to be clear about what we <strong>don't</strong> collect:
            </p>
            <ul>
              <li>Real names or personal identification</li>
              <li>Physical addresses or phone numbers</li>
              <li>Payment information (until monetization features are enabled)</li>
              <li>Social security numbers or government IDs</li>
              <li>Chat messages or in-game communications (we don't have text/voice chat)</li>
              <li>
                <strong>Write-in card content after game sessions end</strong>
              </li>
              <li>
                <strong>Historical record of your write-in cards or custom decks</strong>
              </li>
              <li>Long-term tracking or behavioural profiling</li>
              <li>Third-party social media data</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. How We Use Your Information</h2>
            <p>We use the collected information for:</p>
            <ul>
              <li>
                <strong>Game Operation:</strong> Managing game sessions, room creation, and player
                connections
              </li>
              <li>
                <strong>Account Management:</strong> Maintaining your OAuth user account and
                persistent identity
              </li>
              <li>
                <strong>Reconnection:</strong> Allowing you to resume games and maintain scores
                across devices
              </li>
              <li>
                <strong>Authentication:</strong> Verifying your identity using JWT tokens and OAuth
                providers
              </li>
              <li>
                <strong>Security:</strong> Rate limiting, abuse prevention, and protecting against
                attacks
              </li>
              <li>
                <strong>Communication:</strong> Responding to your inquiries and feedback
              </li>
              <li>
                <strong>Improvement:</strong> Understanding how the game is used to make it better
              </li>
              <li>
                <strong>Compliance:</strong> Meeting legal obligations and enforcing our Terms of
                Service
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>5. Write-In Cards and Custom Deck Data</h2>

            <h3>Invite-Only Rooms (Privacy-First Design)</h3>
            <p className="highlight-text">
              <strong>All rooms are invite-only</strong> - there is no public matchmaking. You
              control who joins your game by sharing the room code.
            </p>
            <ul>
              <li>This reduces exposure to strangers and unwanted interactions</li>
              <li>Room codes expire when games end (not reusable)</li>
            </ul>

            <h3>Write-In Cards</h3>
            <ul>
              <li>Write-in card text is stored temporarily during active game sessions only</li>
              <li>
                Write-in content is deleted when:
                <ul>
                  <li>The game round ends</li>
                  <li>All players leave the room</li>
                  <li>The game session terminates</li>
                </ul>
              </li>
              <li>We do not retain a historical record of write-in cards you create</li>
              <li>
                Write-in content is not linked to your personal identity (only to your session
                username)
              </li>
            </ul>

            <h3>Custom Decks (when implemented)</h3>
            <ul>
              <li>Custom deck content uploaded by users</li>
              <li>Stored only while actively in use during a game session</li>
              <li>May be stored locally in your browser for re-use across sessions</li>
              <li>Not permanently stored on our servers</li>
              <li>Not linked to personally identifiable information</li>
            </ul>

            <h3>Card Attribution</h3>
            <ul>
              <li>
                We track the source of each card (Official, Write-in, or Custom Deck) during
                gameplay
              </li>
              <li>This metadata is displayed to all players via our card attribution system</li>
              <li>Attribution data is temporary and deleted with the game session</li>
            </ul>

            <h3>Law Enforcement</h3>
            <ul>
              <li>Write-in content is deleted immediately when games end</li>
              <li>We cannot retrieve historical write-in content from ended sessions</li>
              <li>
                If authorities contact us about an active session, we may provide: IP address,
                timestamp, room code
              </li>
              <li>We do not actively monitor or moderate write-in content</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>6. Data Storage and Retention</h2>

            <h3>6.1 Temporary Data</h3>
            <p className="highlight-text">
              <strong>All rooms are invite-only</strong> - there is no public matchmaking. You
              control who joins your game.
            </p>
            <ul>
              <li>
                <strong>Game Sessions:</strong> Deleted immediately when game ends or all players
                leave
              </li>
              <li>
                <strong>Room Codes:</strong> Temporary and not stored long-term
              </li>
              <li>
                <strong>Card Selections:</strong> Not retained after round completion
              </li>
              <li>
                <strong>Write-In Card Text:</strong> Deleted when round ends or game session
                terminates
              </li>
              <li>
                <strong>Custom Deck Content:</strong> Deleted when game session ends (unless cached
                in your browser locally)
              </li>
            </ul>

            <h3>6.2 Persistent Data</h3>

            <h4>Authenticated Users (OAuth)</h4>
            <ul>
              <li>
                <strong>User Profile:</strong> Email, username, and OAuth user ID retained for 90
                days after last activity
              </li>
              <li>
                <strong>Game Data:</strong> Scores, saved games, and player hands retained for 90
                days of inactivity
              </li>
              <li>
                <strong>JWT Tokens:</strong> Expire after 7 days (configurable) and must be
                refreshed
              </li>
              <li>
                <strong>Last Activity Timestamp:</strong> Tracked for all authenticated users to
                enable automatic cleanup
              </li>
            </ul>

            <h4>Guest Users</h4>
            <ul>
              <li>
                <strong>Temporary ID:</strong> Generated for the session, deleted when browser
                closes
              </li>
              <li>
                <strong>No Persistent Storage:</strong> Guest game data is lost when session ends
              </li>
              <li>
                <strong>No Cross-Device Access:</strong> Cannot resume games from different devices
              </li>
            </ul>

            <h4>System Data</h4>
            <ul>
              <li>
                <strong>Rate Limiting Data:</strong> IP addresses retained for up to 1 hour for
                abuse prevention
              </li>
              <li>
                <strong>Contact Form Submissions:</strong> Stored until inquiry is resolved, then
                deleted
              </li>
              <li>
                <strong>Browser localStorage:</strong> Stored locally on your device until you clear
                it
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>7. Data Sharing and Disclosure</h2>
            <p className="highlight-text">
              <strong>We do not sell, rent, or trade your personal information.</strong>
            </p>
            <p>We may share information only in these limited circumstances:</p>
            <ul>
              <li>
                <strong>Service Providers:</strong> Third-party services that help us operate (e.g.,
                Formspree for contact forms)
              </li>
              <li>
                <strong>Legal Requirements:</strong> If required by law or to protect rights and
                safety
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger or acquisition (with
                notice)
              </li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>8. Third-Party Services</h2>
            <p>We use the following third-party services:</p>
            <ul>
              <li>
                <strong>Vercel:</strong> Website hosting and deployment
              </li>
              <li>
                <strong>Formspree:</strong> Contact form submissions (see their privacy policy at
                formspree.io/legal/privacy-policy)
              </li>
              <li>
                <strong>Google Analytics 4:</strong> Usage analytics and site improvement (see
                details below)
              </li>
            </ul>
            <p>These services have their own privacy policies and data handling practices.</p>

            <h3>8.1 Google Analytics</h3>
            <p>
              We use Google Analytics 4 to understand how our game is used and improve the user
              experience.
            </p>

            <h4>Information Collected by Google Analytics:</h4>
            <ul>
              <li>
                <strong>Page views and navigation:</strong> Which pages you visit and how you
                navigate the site
              </li>
              <li>
                <strong>Game events:</strong> Room creation, joins, rounds played, game completion
                (aggregate data only)
              </li>
              <li>
                <strong>Technical information:</strong> Browser type, device type, screen resolution
              </li>
              <li>
                <strong>Approximate location:</strong> City/region level (derived from IP address)
              </li>
              <li>
                <strong>IP address:</strong> Temporarily collected by Google for location
                approximation
              </li>
            </ul>

            <h4>What We DON'T Track via Analytics:</h4>
            <ul>
              <li>Write-in card content or custom deck text</li>
              <li>Individual card selections during gameplay</li>
              <li>Your real name or email (unless you contact us separately)</li>
              <li>Long-term behavioural profiles or personal identification</li>
            </ul>

            <h4>Your Analytics Choices:</h4>
            <ul>
              <li>
                <strong>Browser extensions:</strong> You can block Google Analytics using browser
                extensions like uBlock Origin or Privacy Badger
              </li>
              <li>
                <strong>Google opt-out:</strong> Use Google's{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  opt-out browser add-on
                </a>
              </li>
              <li>
                <strong>No impact on gameplay:</strong> Blocking analytics does not affect your
                ability to play
              </li>
            </ul>

            <p>
              For more information about how Google handles your data, see{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google's Privacy Policy
              </a>
              .
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Your Privacy Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>
                <strong>Access:</strong> Request what information we have about you
              </li>
              <li>
                <strong>Deletion:</strong> Delete your account and all associated data via the{' '}
                <Link to="/settings">Settings page</Link> (for OAuth users) or{' '}
                <Link to="/contact">contact us</Link>
              </li>
              <li>
                <strong>Correction:</strong> Request correction of inaccurate information
              </li>
              <li>
                <strong>Opt-Out:</strong> Stop using the service at any time
              </li>
              <li>
                <strong>Data Portability:</strong> Request a copy of your data (if applicable)
              </li>
            </ul>
            <p>
              <strong>For OAuth users:</strong> Visit the <Link to="/settings">Settings page</Link>{' '}
              to delete your account. This will permanently remove all your data from our systems.
            </p>
            <p>
              <strong>For other requests:</strong> <Link to="/contact">Use our contact form</Link>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Data Security</h2>
            <p>We implement security measures to protect your information:</p>
            <ul>
              <li>
                <strong>HTTPS Encryption:</strong> All data transmitted is encrypted
              </li>
              <li>
                <strong>Rate Limiting:</strong> Protection against automated attacks
              </li>
              <li>
                <strong>Input Validation:</strong> Sanitization of all user inputs
              </li>
              <li>
                <strong>Minimal Data Collection:</strong> We only collect what's necessary
              </li>
              <li>
                <strong>Temporary Storage:</strong> Most data is not permanently stored
              </li>
            </ul>
            <p className="legal-text">
              However, no method of transmission over the internet is 100% secure. We cannot
              guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Children's Privacy</h2>
            <p className="highlight-text">
              <strong>Our Service is not intended for anyone under 18 years of age.</strong>
            </p>
            <p>
              We do not knowingly collect information from anyone under 18. If you are a parent or
              guardian and believe your child has provided us with personal information, please
              contact us immediately so we can delete it.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12. International Users</h2>
            <p>
              Our service is operated in the United States. If you are accessing from outside the
              U.S., your information may be transferred to, stored, and processed in the U.S. where
              data protection laws may differ from your country's laws.
            </p>
          </section>

          <section className="privacy-section">
            <h2>13. Recent Changes & Future Features</h2>

            <h3>Recently Implemented (as of November 2025)</h3>
            <ul>
              <li>
                <strong>OAuth Authentication:</strong> Google and Discord login now fully
                implemented
              </li>
              <li>
                <strong>JWT Tokens:</strong> Secure authentication and persistent user identity
              </li>
              <li>
                <strong>Account Deletion:</strong> Self-service account deletion via Settings page
              </li>
              <li>
                <strong>90-Day Auto-Cleanup:</strong> Automatic deletion of inactive user data
              </li>
            </ul>

            <h3>Planned Future Features</h3>
            <p>When we implement additional features, we may collect:</p>
            <ul>
              <li>
                <strong>Payment Information:</strong> If/when we implement token purchases (handled
                by Stripe, not stored by us)
              </li>
              <li>
                <strong>Enhanced Game Statistics:</strong> Win rates, favourite cards, etc.
              </li>
              <li>
                <strong>Saved Custom Card Decks:</strong> User-created content that persists across
                sessions
              </li>
            </ul>
            <p>We will update this Privacy Policy before implementing any of these features.</p>
          </section>

          <section className="privacy-section">
            <h2>14. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this
              page with an updated "Last Updated" date. Your continued use of the Service after
              changes constitutes acceptance of the updated policy.
            </p>
            <p>
              For significant changes, we may provide additional notice through the game interface
              or via email (if we have your contact information).
            </p>
          </section>

          <section className="privacy-section">
            <h2>15. California Privacy Rights</h2>
            <p>
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA):
            </p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information (we don't sell data)</li>
              <li>Right to access your personal information</li>
              <li>Right to equal service and price (we don't discriminate)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>16. European Users (GDPR)</h2>
            <p>
              If you are in the European Economic Area (EEA), you have rights under the General Data
              Protection Regulation (GDPR), including:
            </p>
            <ul>
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>17. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please{' '}
              <Link to="/contact">use our contact form</Link>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>18. User Responsibilities for Write-In Content</h2>

            <h3>Your Responsibility</h3>
            <ul>
              <li>You are solely responsible for the content of write-in cards you create</li>
              <li>You should not include personally identifiable information in write-in cards</li>
              <li>You should not create write-in content that violates our Terms of Service</li>
            </ul>

            <h3>What We Cannot Guarantee</h3>
            <ul>
              <li>Other players may screenshot your write-in cards</li>
              <li>Screenshots may include your username and the write-in content</li>
              <li>We cannot control what happens to screenshots outside our platform</li>
            </ul>

            <h3>Our Limited Control</h3>
            <ul>
              <li>We do not pre-screen or moderate write-in content</li>
              <li>We cannot edit or remove write-in content from other players' devices</li>
              <li>We cannot prevent screenshots or sharing of write-in content</li>
              <li>
                Our card attribution system helps identify content source but cannot prevent misuse
              </li>
              <li>All rooms are invite-only - you control who joins your game</li>
            </ul>

            <h3>Best Practices</h3>
            <ul>
              <li>Treat write-in cards as public content visible to all players</li>
              <li>Assume anything you write could be screenshot</li>
              <li>Do not include information you wouldn't want publicly associated with you</li>
              <li>Only invite people you trust to your games</li>
              <li>As host, remove disruptive players from your room</li>
            </ul>
          </section>
        </div>

        <div className="privacy-footer">
          <p>
            <strong>
              By using Carnivores Adapting Terribly LLC, you acknowledge that you have read and
              understood this Privacy Policy.
            </strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
